import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  History as HistoryIcon,
  Send as SendIcon,
  TrendingUp as TrendingUpIcon,
  Visibility as VisibilityIcon,
  WhatsApp as WhatsAppIcon
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography
} from '@mui/material';
import { useEffect, useState } from 'react';
import { api } from '../api/client';
import Layout from '../components/Layout';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`whatsapp-tabpanel-${index}`}
      aria-labelledby={`whatsapp-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const WhatsAppPage = () => {
  // State для табов
  const [tabValue, setTabValue] = useState(0);

  // State для отправки сообщений
  const [messageForm, setMessageForm] = useState({
    message: '',
    messageType: 'text',
    templateType: '',
    clientIds: [],
    sendToAll: false,
    templateParams: {}
  });

  // State для клиентов
  const [clients, setClients] = useState([]);
  const [selectedClients, setSelectedClients] = useState([]);

  // State для истории сообщений
  const [messages, setMessages] = useState([]);
  const [messagesPage, setMessagesPage] = useState(1);
  const [messagesTotal, setMessagesTotal] = useState(0);

  // State для статистики
  const [stats, setStats] = useState({});

  // State для шаблонов
  const [templates, setTemplates] = useState({});
  const [templatePreview, setTemplatePreview] = useState('');

  // State для диалогов
  const [previewDialog, setPreviewDialog] = useState(false);
  const [sendDialog, setSendDialog] = useState(false);
  const [sendResults, setSendResults] = useState(null);

  // State для загрузки
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ show: false, type: 'info', message: '' });
  // Setup state
  const [setup, setSetup] = useState({ senderPhone: '', verified: false });
  const [verifyPhone, setVerifyPhone] = useState('');
  const [verifyCode, setVerifyCode] = useState('');

  // Загрузка данных при монтировании
  useEffect(() => {
    loadClients();
    loadMessages();
    loadStats();
    loadTemplates();
    loadSetup();
  }, []);
  const loadSetup = async () => {
    try {
      const response = await api.get('/whatsapp/setup');
      setSetup(response.data);
      setVerifyPhone(response.data.senderPhone || '');
    } catch (error) {
      // тихо
    }
  };

  const requestVerification = async () => {
    if (!verifyPhone) {
      showAlert('warning', 'Введите номер телефона');
      return;
    }
    try {
      setLoading(true);
      await api.post('/whatsapp/setup/verify', { phone: verifyPhone });
      showAlert('success', 'Код подтверждения отправлен в WhatsApp');
    } catch (e) {
      showAlert('error', e.response?.data?.message || 'Не удалось отправить код');
    } finally {
      setLoading(false);
    }
  };

  const confirmVerification = async () => {
    if (!verifyCode) {
      showAlert('warning', 'Введите код подтверждения');
      return;
    }
    try {
      setLoading(true);
      await api.post('/whatsapp/setup/confirm', { code: verifyCode });
      await loadSetup();
      showAlert('success', 'Номер подтвержден');
    } catch (e) {
      showAlert('error', e.response?.data?.message || 'Неверный код');
    } finally {
      setLoading(false);
    }
  };

  const showAlert = (type, message) => {
    setAlert({ show: true, type, message });
    setTimeout(() => setAlert({ show: false, type: 'info', message: '' }), 5000);
  };

  const loadClients = async () => {
    try {
      const response = await api.get('/clients', { params: { per_page: 1000 } });
      // Интерсептор превращает { data: [...] } в массив
      setClients(response.data || []);
    } catch (error) {
      showAlert('error', 'Ошибка загрузки клиентов');
    }
  };

  const loadMessages = async (page = 1) => {
    try {
      setLoading(true);
      const response = await api.get('/whatsapp/messages', { params: { page, per_page: 20 } });
      // Интерсептор превращает тело с данными в массив и добавляет response.pagination
      setMessages(response.data || []);
      setMessagesTotal(response.pagination?.total || 0);
      setMessagesPage(response.pagination?.page || page);
    } catch (error) {
      showAlert('error', 'Ошибка загрузки истории сообщений');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await api.get('/whatsapp/stats');
      setStats(response.data);
    } catch (error) {
      showAlert('error', 'Ошибка загрузки статистики');
    }
  };

  const loadTemplates = async () => {
    try {
      const response = await api.get('/whatsapp/templates');
      setTemplates(response.data);
    } catch (error) {
      showAlert('error', 'Ошибка загрузки шаблонов');
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleMessageFormChange = (field, value) => {
    setMessageForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleClientSelection = (clientId) => {
    setSelectedClients(prev => {
      if (prev.includes(clientId)) {
        return prev.filter(id => id !== clientId);
      } else {
        return [...prev, clientId];
      }
    });
  };

  const handleSelectAllClients = (checked) => {
    if (checked) {
      setSelectedClients(clients.map(c => c.id));
    } else {
      setSelectedClients([]);
    }
  };

  const previewTemplate = async () => {
    if (!messageForm.templateType) {
      showAlert('warning', 'Выберите тип шаблона');
      return;
    }

    try {
      const response = await api.post('/whatsapp/templates/preview', {
        templateType: messageForm.templateType,
        params: messageForm.templateParams,
        message: messageForm.message
      });
      setTemplatePreview(response.data.preview);
      setPreviewDialog(true);
    } catch (error) {
      showAlert('error', 'Ошибка создания превью шаблона');
    }
  };

  const sendMessages = async () => {
    try {
      setLoading(true);

      const payload = {
        message: messageForm.message,
        messageType: messageForm.messageType,
        templateType: messageForm.templateType,
        sendToAll: messageForm.sendToAll,
        clientIds: messageForm.sendToAll ? [] : selectedClients,
        templateParams: messageForm.templateParams
      };

      const response = await api.post('/whatsapp/send-bulk', payload);
      setSendResults(response.data);
      setSendDialog(true);

      // Обновляем данные
      loadMessages();
      loadStats();

      showAlert('success', response.data.message);
    } catch (error) {
      showAlert('error', error.response?.data?.message || 'Ошибка отправки сообщений');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'sent':
      case 'delivered':
        return 'success';
      case 'read':
        return 'primary';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'sent':
        return 'Отправлено';
      case 'delivered':
        return 'Доставлено';
      case 'read':
        return 'Прочитано';
      case 'failed':
        return 'Ошибка';
      case 'pending':
        return 'В очереди';
      default:
        return status;
    }
  };

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          <WhatsAppIcon sx={{ mr: 2, verticalAlign: 'middle', color: '#25D366' }} />
          WhatsApp Рассылки
        </Typography>

        {alert.show && (
          <Alert severity={alert.type} sx={{ mb: 2 }} onClose={() => setAlert({ show: false })}>
            {alert.message}
          </Alert>
        )}

        <Paper sx={{ mt: 2 }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label="Настройка" icon={<WhatsAppIcon />} />
            <Tab label="Новая рассылка" icon={<SendIcon />} />
            <Tab label="История" icon={<HistoryIcon />} />
            <Tab label="Статистика" icon={<TrendingUpIcon />} />
          </Tabs>

          {/* Настройка */}
          <TabPanel value={tabValue} index={0}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Номер отправителя
                    </Typography>
                    <TextField
                      fullWidth
                      label="Ваш номер WhatsApp в формате +79991234567"
                      value={verifyPhone}
                      onChange={(e) => setVerifyPhone(e.target.value)}
                      margin="normal"
                    />
                    <Button variant="outlined" onClick={requestVerification} disabled={loading || !verifyPhone}>
                      Отправить код подтверждения
                    </Button>
                    <Divider sx={{ my: 2 }} />
                    <TextField
                      fullWidth
                      label="Код подтверждения"
                      value={verifyCode}
                      onChange={(e) => setVerifyCode(e.target.value)}
                      margin="normal"
                    />
                    <Button variant="contained" onClick={confirmVerification} disabled={loading || !verifyCode}>
                      Подтвердить номер
                    </Button>
                    {setup.verified && (
                      <Alert severity="success" sx={{ mt: 2 }}>
                        Номер подтвержден: {setup.senderPhone}
                      </Alert>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </TabPanel>

          {/* Новая рассылка */}
          <TabPanel value={tabValue} index={1}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Сообщение</Typography>

                    <FormControl fullWidth margin="normal">
                      <InputLabel>Тип шаблона</InputLabel>
                      <Select
                        value={messageForm.templateType}
                        onChange={(e) => handleMessageFormChange('templateType', e.target.value)}
                      >
                        <MenuItem value="">Без шаблона</MenuItem>
                        {Object.keys(templates).map(key => (
                          <MenuItem key={key} value={key}>
                            {templates[key].name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <TextField
                      fullWidth
                      multiline
                      rows={6}
                      label="Текст сообщения"
                      value={messageForm.message}
                      onChange={(e) => handleMessageFormChange('message', e.target.value)}
                      margin="normal"
                      placeholder="Введите текст сообщения. Используйте {name} для вставки имени клиента."
                    />

                    {messageForm.templateType && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Параметры шаблона:
                        </Typography>
                        {templates[messageForm.templateType]?.params?.map(param => (
                          <TextField
                            key={param}
                            fullWidth
                            size="small"
                            label={param}
                            value={messageForm.templateParams[param] || ''}
                            onChange={(e) => handleMessageFormChange('templateParams', {
                              ...messageForm.templateParams,
                              [param]: e.target.value
                            })}
                            margin="dense"
                          />
                        ))}
                        <Button
                          variant="outlined"
                          startIcon={<VisibilityIcon />}
                          onClick={previewTemplate}
                          sx={{ mt: 1 }}
                        >
                          Предпросмотр
                        </Button>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Получатели</Typography>

                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={messageForm.sendToAll}
                          onChange={(e) => handleMessageFormChange('sendToAll', e.target.checked)}
                        />
                      }
                      label="Отправить всем клиентам"
                    />

                    {!messageForm.sendToAll && (
                      <Box>
                        <Box sx={{ mb: 2 }}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={selectedClients.length === clients.length}
                                indeterminate={selectedClients.length > 0 && selectedClients.length < clients.length}
                                onChange={(e) => handleSelectAllClients(e.target.checked)}
                              />
                            }
                            label="Выбрать всех"
                          />
                          <Typography variant="body2" color="textSecondary">
                            Выбрано: {selectedClients.length} из {clients.length}
                          </Typography>
                        </Box>

                        <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                          {clients.map(client => (
                            <FormControlLabel
                              key={client.id}
                              control={
                                <Checkbox
                                  checked={selectedClients.includes(client.id)}
                                  onChange={() => handleClientSelection(client.id)}
                                  disabled={!client.phone}
                                />
                              }
                              label={
                                <Box>
                                  <Typography variant="body2">
                                    {client.name}
                                  </Typography>
                                  <Typography variant="caption" color="textSecondary">
                                    {client.phone || 'Нет номера телефона'}
                                  </Typography>
                                </Box>
                              }
                            />
                          ))}
                        </Box>
                      </Box>
                    )}

                    <Divider sx={{ my: 2 }} />

                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<SendIcon />}
                      onClick={sendMessages}
                      disabled={loading || (!messageForm.sendToAll && selectedClients.length === 0) || !messageForm.message}
                      fullWidth
                    >
                      {loading ? 'Отправка...' : 'Отправить рассылку'}
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </TabPanel>

          {/* История сообщений */}
          <TabPanel value={tabValue} index={2}>
            {loading && <LinearProgress />}
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Дата</TableCell>
                    <TableCell>Клиент</TableCell>
                    <TableCell>Телефон</TableCell>
                    <TableCell>Сообщение</TableCell>
                    <TableCell>Статус</TableCell>
                    <TableCell>Провайдер</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {messages.map((message) => (
                    <TableRow key={message.id}>
                      <TableCell>
                        {new Date(message.created_at).toLocaleString()}
                      </TableCell>
                      <TableCell>{message.clientId || '-'}</TableCell>
                      <TableCell>{message.phoneNumber}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {message.messageContent}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getStatusText(message.status)}
                          color={getStatusColor(message.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{message.provider || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>

          {/* Статистика */}
          <TabPanel value={tabValue} index={3}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Всего сообщений
                    </Typography>
                    <Typography variant="h4">
                      {stats.totalMessages || 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Отправлено
                    </Typography>
                    <Typography variant="h4" color="success.main">
                      {stats.sent || 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Доставлено
                    </Typography>
                    <Typography variant="h4" color="primary.main">
                      {stats.delivered || 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Процент доставки
                    </Typography>
                    <Typography variant="h4" color="info.main">
                      {stats.deliveryRate || 0}%
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </TabPanel>
        </Paper>

        {/* Диалог предпросмотра */}
        <Dialog open={previewDialog} onClose={() => setPreviewDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Предпросмотр сообщения</DialogTitle>
          <DialogContent>
            <Paper sx={{ p: 2, bgcolor: 'grey.100' }}>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                {templatePreview}
              </Typography>
            </Paper>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPreviewDialog(false)}>Закрыть</Button>
          </DialogActions>
        </Dialog>

        {/* Диалог результатов отправки */}
        <Dialog open={sendDialog} onClose={() => setSendDialog(false)} maxWidth="md" fullWidth>
          <DialogTitle>Результаты рассылки</DialogTitle>
          <DialogContent>
            {sendResults && (
              <Box>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Отправлено: {sendResults.summary?.sent}, Ошибок: {sendResults.summary?.failed}
                </Alert>

                <List>
                  {sendResults.details?.map((result, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        {result.success ? (
                          <CheckCircleIcon color="success" />
                        ) : (
                          <ErrorIcon color="error" />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={`${result.name} (${result.phone})`}
                        secondary={result.success ? 'Отправлено успешно' : result.error}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSendDialog(false)}>Закрыть</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Layout>
  );
};

export default WhatsAppPage;


